/**
 * State and presence helpers shared by the layout primitives (tabs,
 * accordion, collapsible, sidebar). The form controls have their own in
 * `_control.ts`.
 *
 * - {@link createControllable}: one value that is either owned by the primitive
 *   (`defaultValue`) or by the caller (`value` + `onValueChange`), Radix's
 *   `useControllableState` without a framework.
 * - {@link directionOf}: reading order for RTL-aware arrow keys.
 * - {@link isFlaggedDisabled} / {@link applyDisabled}: disabled flags that
 *   distinguish the author's markup from what a primitive wrote.
 * - {@link syncContent}: Radix `Presence` + the `--radix-*-content-height/width`
 *   measurement that accordion and collapsible animate with.
 */
import { exitWhenDone } from './_presence.js';
/** Explicit `dir` wins, then the nearest `[dir]` ancestor, then `ltr`. */
export function directionOf(element, override) {
    if (override)
        return override;
    return element.closest('[dir]')?.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr';
}
export function createControllable(defaultValue, onChange, equals = Object.is) {
    let internal = defaultValue;
    let controlledValue;
    let controlled = false;
    const get = () => (controlled ? controlledValue : internal);
    return {
        get,
        get controlled() {
            return controlled;
        },
        request(next) {
            if (equals(get(), next))
                return false;
            if (!controlled)
                internal = next;
            onChange(next);
            return true;
        },
        control(value) {
            if (value === undefined) {
                if (controlled)
                    internal = controlledValue;
                controlled = false;
                controlledValue = undefined;
                return;
            }
            controlled = true;
            controlledValue = value;
        },
    };
}
const inheritedDisabled = new WeakSet();
/**
 * True when the markup (not this module) flagged `element` disabled:
 * `disabled` or `data-disabled`. Flags written by {@link applyDisabled}
 * never read back as the author's, so a root can disable then re-enable
 * its items without leaving them stuck.
 */
export function isFlaggedDisabled(element) {
    if (inheritedDisabled.has(element))
        return false;
    if (element.hasAttribute('disabled'))
        return true;
    const data = element.getAttribute('data-disabled');
    return data !== null && data !== 'false';
}
/**
 * Mirrors a disabled state onto an element the way Radix renders it
 * (`data-disabled`, plus `disabled` on buttons). Never overrides or clears a
 * flag the author wrote in markup.
 */
export function applyDisabled(element, disabled) {
    if (isFlaggedDisabled(element))
        return;
    if (disabled) {
        inheritedDisabled.add(element);
        element.setAttribute('data-disabled', '');
        if (element instanceof HTMLButtonElement)
            element.disabled = true;
    }
    else if (inheritedDisabled.delete(element)) {
        element.removeAttribute('data-disabled');
        if (element instanceof HTMLButtonElement)
            element.disabled = false;
    }
}
const pendingExit = new WeakMap();
/**
 * Measures `element` at its natural size with animation and transition
 * suspended, and publishes `--radix-<prefix>-content-height|width` on it, the
 * variables the shadcn keyframes (`accordion-down`, `collapsible-down`)
 * animate between. The element must be rendered (not `hidden`).
 */
function publishSize(element, prefixes) {
    const { style } = element;
    const saved = {
        animationName: style.animationName,
        transitionDuration: style.transitionDuration,
        height: style.height,
        width: style.width,
    };
    style.transitionDuration = '0s';
    style.animationName = 'none';
    style.height = '';
    style.width = '';
    const rect = element.getBoundingClientRect();
    for (const prefix of prefixes) {
        style.setProperty(`--radix-${prefix}-content-height`, `${rect.height}px`);
        style.setProperty(`--radix-${prefix}-content-width`, `${rect.width}px`);
    }
    style.transitionDuration = saved.transitionDuration;
    style.animationName = saved.animationName;
    style.height = saved.height;
    style.width = saved.width;
}
/** Radix skips the mount animation: hold `animation-name: none` until the next frame. */
function suppressAnimationOnce(element) {
    const previous = element.style.animationName;
    element.style.animationName = 'none';
    const restore = () => {
        element.style.animationName = previous;
        if (element.getAttribute('style') === '')
            element.removeAttribute('style');
    };
    if (typeof requestAnimationFrame === 'function')
        requestAnimationFrame(restore);
    else
        restore();
}
/**
 * Applies open/closed to a collapsible region the way Radix `Presence` does:
 * `data-state` flips immediately, the size variables are measured, and on
 * close `hidden` is only set once any exit animation has finished (or at once
 * when none is running). Opening cancels a pending hide.
 */
export function syncContent(element, open, options) {
    pendingExit.get(element)?.();
    const wasOpen = element.getAttribute('data-state') === 'open';
    if (open) {
        element.removeAttribute('hidden');
        publishSize(element, options.prefixes);
        element.setAttribute('data-state', 'open');
        if (options.initial)
            suppressAnimationOnce(element);
        return;
    }
    if (!element.hasAttribute('hidden') && wasOpen)
        publishSize(element, options.prefixes);
    element.setAttribute('data-state', 'closed');
    if (options.initial || !wasOpen) {
        element.setAttribute('hidden', '');
        return;
    }
    let finished = false;
    const cancel = exitWhenDone(element, () => {
        finished = true;
        pendingExit.delete(element);
        if (element.getAttribute('data-state') === 'closed')
            element.setAttribute('hidden', '');
    });
    if (!finished)
        pendingExit.set(element, cancel);
}
